/**
 * Chat interno — a conversa da equipe.
 *
 * ## O que esta tela não é
 *
 * Não é o comentário interno de um atendimento — aquele fica dentro da conversa, no inbox, e continua sendo
 * o lugar certo para anotar algo sobre um cliente. Aqui é falar com uma pessoa da equipe.
 *
 * ## O detalhe que faz este chat valer a pena
 *
 * **Citar um atendimento.** "Olha esse aí" é o que a equipe realmente faz, e a citação vira link — o mesmo
 * motivo de a conversa aberta ter virado URL no inbox: mandar "olha esse atendimento" para um colega era
 * impossível antes disso.
 *
 * ## Abrir o fio É marcar como lido
 *
 * A tela não chama nada além de abrir. Um botão "marcar como lido" existiria só para alguém esquecer de
 * clicar, e o contador ficaria aceso para sempre.
 */

import { useId, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Avatar, EstadoDaFalha, falhaDe } from "@hugi/ui";
import { CabecalhoDaTela, Carregando, Dica, GradeDeCartoes, PaginaInteira, Vazio } from "@hugi/ui/kit";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { enviarInterna, useChat, useFioInterno } from "./dados.js";
import "./hugi-chat.css";

/*
 * A FORMA (spec 22, 31/10): família MESA DE TRABALHO — `PaginaInteira` + `CabecalhoDaTela` + `GradeDeCartoes` com
 * duas colunas: a caixa de entrada da equipe e o fio. A lista e o fio rolam por dentro; o campo de mensagem fica no
 * rodapé do cartão, fora do que rola. No celular, um cartão por vez: escolher alguém abre o fio, e o "Voltar" (que
 * só aparece aí) devolve à lista.
 */
function Pagina(props: { readonly children: ReactNode }) {
  return (
    <PaginaInteira rotulo="Chat da equipe" classe="hugi-chat">
      <CabecalhoDaTela
        icone={<MessageCircle size={16} />}
        titulo="Chat da equipe"
        dica="Conversa entre vocês. O cliente nunca vê nada daqui."
      />
      {props.children}
    </PaginaInteira>
  );
}

export function Chat() {
  const cliente = useQueryClient();
  const q = useChat();
  const [comQuem, setComQuem] = useState<string | null>(null);
  const [cartao, setCartao] = useState<"equipe" | "conversa">("equipe");
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const idDoMotivo = useId();

  if (q.isPending) return <Pagina><Carregando>Carregando as conversas da equipe</Carregando></Pagina>;
  if (q.isError) return <Pagina><EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} /></Pagina>;

  const { conversas, pessoas, eu, podeIniciar } = q.data;

  const nomeDe = (id: string) => pessoas.find((p) => p.id === id)?.nome ?? id;

  const abrir = (id: string) => {
    setComQuem(id);
    setCartao("conversa");
  };

  const enviar = async () => {
    if (!comQuem) return;
    setErro(null);
    const r = await enviarInterna(cliente, { paraId: comQuem, texto });
    if (r.ok) {
      setTexto("");
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível enviar.");
  };

  // --- A caixa de entrada -------------------------------------------------------------------------
  const caixa = (
    <div className="hugi-chat-caixa">
      <ul className="hugi-chat-conversas">
        {conversas.map((c) => (
          <li key={c.comQuem}>
            <button
              type="button"
              onClick={() => abrir(c.comQuem)}
              className={`hugi-chat-conversa${comQuem === c.comQuem ? " is-aberta" : ""}`}
              aria-current={comQuem === c.comQuem ? "true" : undefined}
            >
              <Avatar nome={nomeDe(c.comQuem)} chave={c.comQuem} />
              <span className="hugi-chat-conversa-texto">
                <span className="hugi-chat-conversa-nome">{nomeDe(c.comQuem)}</span>
                <span className="hugi-chat-conversa-previa">
                  {c.minhaFoiAUltima ? "Você: " : ""}
                  {c.previa}
                </span>
              </span>
              {c.naoLidas > 0 && (
                // O número, e não um ponto: "3" e "12" são decisões diferentes sobre parar o que se está
                // fazendo, e um ponto obriga a abrir para descobrir qual é o caso.
                <span className="hugi-chat-nao-lidas">{c.naoLidas}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <div className="hugi-chat-comecar">
        <p className="hugi-chat-comecar-titulo">Começar uma conversa</p>
        {!podeIniciar ? (
          // A regra é do tenant (Ajustes → quem pode começar um chat). Dizer aqui, em vez de deixar
          // a pessoa escolher alguém e levar 403: quem lê sabe por que não há seletor.
          <p className="hugi-chat-nota">
            Nesta conta, quem começa uma conversa é o supervisor ou o administrador. Quando alguém falar com você, a conversa aparece aqui e você responde.
          </p>
        ) : pessoas.length === 0 ? (
          <p className="hugi-chat-nota">
            Você é a única pessoa nesta conta. Quando houver mais gente atendendo, ela aparece aqui.
          </p>
        ) : (
          <select
            className="hugi-chat-seletor"
            aria-label="Começar uma conversa com"
            value=""
            onChange={(e) => e.target.value && abrir(e.target.value)}
          >
            <option value="">Escolha uma pessoa</option>
            {pessoas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome ?? p.id}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );

  // --- O fio ----------------------------------------------------------------------------------------
  const vazio = texto.trim() === "";
  const compositor = comQuem ? (
    <div className="hugi-chat-compositor">
      {erro && <p className="hugi-chat-erro" role="alert">{erro}</p>}
      <div className="hugi-chat-compositor-linha">
        <input
          className="hugi-chat-campo"
          value={texto}
          maxLength={4000}
          placeholder={`Mensagem para ${nomeDe(comQuem)}`}
          aria-label={`Mensagem para ${nomeDe(comQuem)}`}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void enviar();
            }
          }}
        />
        <button
          type="button"
          className="v2-botao is-primario"
          onClick={() => void enviar()}
          disabled={vazio}
          {...(vazio ? { title: "Escreva a mensagem.", "aria-describedby": idDoMotivo } : {})}
        >
          Enviar
        </button>
        {vazio && <span id={idDoMotivo} className="v2-sr">Escreva a mensagem.</span>}
      </div>
    </div>
  ) : undefined;

  return (
    <Pagina>
      <GradeDeCartoes
        rotulo="Chat da equipe"
        colunas="minmax(260px, 18rem) minmax(0, 1fr)"
        ativo={cartao}
        aoTrocar={(id) => setCartao(id as "equipe" | "conversa")}
        cartoes={[
          { id: "equipe", titulo: "Equipe", conteudo: caixa },
          {
            id: "conversa",
            titulo: comQuem ? nomeDe(comQuem) : "Conversa",
            rotuloCurto: "Conversa",
            ...(comQuem
              ? {
                  ferramentas: (
                    // Só na tela estreita, onde a lista não está do lado: sem isto não há como voltar a ela.
                    <button
                      type="button"
                      className="v2-botao is-pequeno hugi-chat-voltar"
                      onClick={() => {
                        setComQuem(null);
                        setCartao("equipe");
                      }}
                    >
                      <ArrowLeft size={14} aria-hidden="true" />
                      Voltar
                    </button>
                  ),
                }
              : {}),
            conteudo: comQuem === null ? (
              <Vazio>Escolha uma conversa<Dica texto="Selecione alguém da equipe na lista, ou comece uma conversa nova." /></Vazio>
            ) : (
              <Fio comQuem={comQuem} eu={eu} />
            ),
            ...(compositor ? { rodape: compositor } : {}),
          },
        ]}
      />
    </Pagina>
  );
}

function Fio({ comQuem, eu }: { comQuem: string; eu: string }) {
  const q = useFioInterno(comQuem);

  if (q.isPending) return <Carregando>Carregando a conversa</Carregando>;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  if (q.data.itens.length === 0) {
    return <Vazio>Nada aqui ainda<Dica texto="Escreva a primeira mensagem abaixo." /></Vazio>;
  }

  return (
    <ol className="hugi-chat-fio">
      {q.data.itens.map((m) => {
        const minha = m.deId === eu;
        return (
          <li key={m.id} className={minha ? "is-minha" : undefined}>
            <div className="hugi-chat-balao">
              {m.texto}
              {m.atendimentoCitadoId && (
                /*
                  A citação vira LINK, e é o motivo de esta tela existir.
                  Colar o protocolo no texto obrigaria o colega a procurar a conversa à mão — que é
                  exatamente o que a URL do inbox passou a resolver.
                */
                <Link href={`/atender/${m.atendimentoCitadoId}`} className="hugi-chat-citacao">
                  Abrir {m.protocoloCitado ?? "a conversa"}
                </Link>
              )}
            </div>
            <p className="hugi-chat-quando">
              {new Date(m.criadoEm).toLocaleString("pt-BR")}
              {minha && m.lidoEm ? " · lida" : ""}
            </p>
          </li>
        );
      })}
    </ol>
  );
}
